import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Download, Truck, ShieldCheck, Plus, Minus, ShoppingBag, ArrowLeft, ZoomIn } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { LightningBolt } from "../components/BrandAccents";
import { StarRating } from "../components/StarRating";
import ProductReviews from "../components/ProductReviews";
import SEO from "../components/SEO";
import ImageZoomLightbox from "../components/ImageZoomLightbox";
import RecentlyViewed, { trackRecentlyViewed } from "../components/RecentlyViewed";
import NotifyBackInStock from "../components/NotifyBackInStock";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { add, setOpen } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`)
      .then(({ data }) => {
        setProduct(data);
        trackRecentlyViewed(data.slug);
        // Auto-select first color if product has color options
        const firstColor = (data.colors && data.colors[0]) || null;
        if (firstColor) {
          setColor(firstColor.name);
          setActiveImage(firstColor.image_url || data.image_url);
        } else {
          setColor("");
          setActiveImage(data.image_url);
        }
        setSize("");
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-[#8a8076]">Loading…</div>;
  if (!product) return (
    <div className="max-w-7xl mx-auto px-6 py-20 text-center">
      <div className="font-script text-3xl text-[#e8478e]">oops</div>
      <h1 className="font-display text-4xl font-black text-[#1c1815]">Not Found</h1>
      <Link to="/shop" className="mt-6 inline-flex items-center gap-1.5 text-[#2eb6a8]"><ArrowLeft size={16}/> Back to shop</Link>
    </div>
  );

  const colorImages = (product.colors || []).map((c) => c.image_url).filter(Boolean);
  const allImages = Array.from(new Set([product.image_url, ...colorImages, ...(product.gallery || [])].filter(Boolean)));
  const current = activeImage || product.image_url;
  const seoTitle = product.meta_title || `${product.title} · Snip Happens Boutique`;
  const seoDesc = product.meta_description || (product.description ? product.description.slice(0, 160) : `Shop ${product.title} at Snip Happens Boutique — trendy designs, boutique finds.`);
  const seoImage = product.image_url && product.image_url.startsWith("http") ? product.image_url : undefined;

  return (
    <div data-testid="product-detail" className="max-w-7xl mx-auto px-6 py-12 sm:py-16">
      <SEO
        title={seoTitle}
        description={seoDesc}
        image={seoImage}
        url={typeof window !== "undefined" ? window.location.href : undefined}
        type="product"
      />
      <Link to="/shop" className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] hover:text-[#2eb6a8] inline-flex items-center gap-1.5 font-bold">
        <ArrowLeft size={14}/> Back to shop
      </Link>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="relative">
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            data-testid="detail-main-image-trigger"
            aria-label="Zoom in on product photo"
            className="group relative w-full aspect-square overflow-hidden rounded-3xl border border-[#e5dccd] bg-white shadow-sm cursor-zoom-in"
          >
            <img src={current} alt={product.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid="detail-main-image"/>
            <span className="absolute bottom-3 right-3 bg-white/90 backdrop-blur text-[#1c1815] rounded-full p-2.5 shadow-md opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all">
              <ZoomIn size={16}/>
            </span>
          </button>
          {product.badge && (
            <span data-testid="detail-badge" className={`absolute top-4 left-4 text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-[0.18em] shadow-md ${
              product.badge === "bestseller" ? "bg-[#e8478e] text-white" : "bg-[#2eb6a8] text-white"
            }`}>
              {product.badge === "bestseller" ? "Best Seller" : "Trending"}
            </span>
          )}
          {allImages.length > 1 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="detail-thumbnails">
              {allImages.map((url, idx) => {
                const isActive = url === current;
                return (
                  <button
                    key={url + idx}
                    type="button"
                    onClick={() => setActiveImage(url)}
                    data-testid={`detail-thumb-${idx}`}
                    className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border-2 transition-all ${
                      isActive ? "border-[#e8478e] shadow-md scale-105" : "border-[#e5dccd] hover:border-[#2eb6a8] opacity-80 hover:opacity-100"
                    }`}
                    aria-label={`Show photo ${idx + 1}`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover"/>
                  </button>
                );
              })}
            </div>
          )}

          {Array.isArray(product.colors) && product.colors.length > 0 && (
            <div className="mt-5" data-testid="color-picker">
              <div className="flex items-baseline justify-between">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Color</div>
                {color && <div className="text-xs text-[#1c1815] font-bold">{color}</div>}
              </div>
              <select
                data-testid="color-select"
                value={color}
                onChange={(e) => {
                  const picked = e.target.value;
                  setColor(picked);
                  const c = product.colors.find((cc) => cc.name === picked);
                  if (c && c.image_url) setActiveImage(c.image_url);
                }}
                className="mt-2 w-full bg-white border-2 border-[#e5dccd] focus:border-[#e8478e] rounded-xl px-4 py-3 text-sm font-bold text-[#1c1815] outline-none cursor-pointer"
              >
                {product.colors.map((c) => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {Array.isArray(product.variants) && product.variants.length > 0 && (
            <div className="mt-5" data-testid="size-picker">
              <div className="flex items-baseline justify-between">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Size</div>
                {size && (product.variants.find((v) => v.size === size)?.stock || 0) <= 0 && (
                  <div className="text-xs text-[#c93875] font-bold">Sold out</div>
                )}
              </div>
              <select
                data-testid="size-select"
                value={size}
                onChange={(e) => setSize(e.target.value)}
                className="mt-2 w-full bg-white border-2 border-[#e5dccd] focus:border-[#e8478e] rounded-xl px-4 py-3 text-sm font-bold text-[#1c1815] outline-none cursor-pointer"
              >
                <option value="">— Choose a size —</option>
                {product.variants.map((v) => {
                  const oos = (v.stock || 0) <= 0;
                  return (
                    <option key={v.size} value={v.size} disabled={oos}>
                      {v.size}{oos ? " — Sold Out" : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8a8076] font-bold">{product.category}</div>
          <h1 data-testid="product-title" className="mt-2 font-display text-4xl sm:text-5xl font-black text-[#1c1815] tracking-tight leading-[1.1]">
            {product.title}
          </h1>
          {product.review_count > 0 && (
            <div className="mt-3 flex items-center gap-2" data-testid="product-rating">
              <StarRating value={product.avg_rating} size={15}/>
              <span className="text-sm text-[#4a4239]">
                <strong className="text-[#1c1815]">{product.avg_rating.toFixed(1)}</strong>
                <span className="text-[#8a8076]"> · {product.review_count} review{product.review_count === 1 ? "" : "s"}</span>
              </span>
            </div>
          )}
          <div className="mt-5 flex items-baseline gap-3">
            <span data-testid="product-price" className="font-display font-extrabold text-4xl text-[#1c1815]">${product.price.toFixed(2)}</span>
            {product.is_digital && (
              <span className="inline-flex items-center gap-1 bg-[#2eb6a8]/10 border border-[#2eb6a8]/40 text-[#1f8d83] text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-[0.18em]">
                <Download size={11}/> Instant Download
              </span>
            )}
          </div>

          {!product.is_digital && (
            <div
              data-testid="ship-time-pill"
              className="mt-5 flex items-center gap-2.5 bg-[#e8478e]/10 border-2 border-[#e8478e]/40 rounded-2xl px-4 py-3"
            >
              <Truck size={16} className="text-[#c93875] flex-shrink-0"/>
              <div className="text-sm leading-tight">
                <div className="font-bold uppercase tracking-[0.18em] text-[#c93875] text-[11px]">Handmade To Order</div>
                <div className="text-[#1c1815]"><strong>3–5 business days</strong> to make &amp; ship</div>
              </div>
            </div>
          )}

          {!product.is_digital && (() => {
            const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
            if (hasVariants) {
              if (!size) return null;
              const v = product.variants.find((x) => x.size === size);
              if (v && (v.stock || 0) <= 0) {
                return <NotifyBackInStock product={product} size={size}/>;
              }
              return null;
            }
            if ((product.stock || 0) <= 0) {
              return <NotifyBackInStock product={product} size={null}/>;
            }
            return null;
          })()}

          <div className="mt-8 flex items-center gap-4">
            <div className="inline-flex items-center bg-white border border-[#e5dccd] rounded-full overflow-hidden">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3.5 py-3.5 hover:bg-[#efe5d4]" data-testid="detail-qty-dec"><Minus size={14}/></button>
              <span data-testid="detail-qty" className="w-10 text-center font-bold text-[#1c1815]">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-3.5 py-3.5 hover:bg-[#efe5d4]" data-testid="detail-qty-inc"><Plus size={14}/></button>
            </div>
            <button
              data-testid="add-to-cart-detail"
              onClick={() => {
                const hasSizes = Array.isArray(product.variants) && product.variants.length > 0;
                const hasColors = Array.isArray(product.colors) && product.colors.length > 0;
                if (hasSizes && !size) { toast.error("Pick a size first ✨"); return; }
                if (hasColors && !color) { toast.error("Pick a color first ✨"); return; }
                const chosenColor = hasColors ? color : null;
                const chosenImg = hasColors
                  ? (product.colors.find((c) => c.name === color)?.image_url || product.image_url)
                  : product.image_url;
                add(product, qty, size || null, { silent: hasSizes || hasColors, color: chosenColor, image_url: chosenImg });
                if (hasSizes || hasColors) {
                  const label = [size && `Size ${size}`, chosenColor].filter(Boolean).join(" · ");
                  toast.success(`Added ${label} to bag ✨`, {
                    action: { label: "View Bag", onClick: () => setOpen(true) },
                  });
                }
                // Low-stock urgency nudge — only after they add to bag
                const remaining = hasSizes
                  ? (product.variants.find((v) => v.size === size)?.stock || 0) - qty
                  : (product.stock || 0) - qty;
                if (!product.is_digital && remaining > 0 && remaining <= 5) {
                  setTimeout(() => {
                    toast.warning(`Hurry — only ${remaining} left!`, { duration: 4500 });
                  }, 350);
                }
              }}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2eb6a8] hover:bg-[#1f8d83] hover:shadow-lg hover:-translate-y-0.5 text-white font-bold uppercase tracking-[0.2em] text-xs py-4 rounded-full transition-all btn-shimmer"
            >
              <ShoppingBag size={16}/> Add to Bag
            </button>
          </div>

          <button
            data-testid="buy-now"
            onClick={() => {
              const hasSizes = Array.isArray(product.variants) && product.variants.length > 0;
              const hasColors = Array.isArray(product.colors) && product.colors.length > 0;
              if (hasSizes && !size) { toast.error("Pick a size first ✨"); return; }
              if (hasColors && !color) { toast.error("Pick a color first ✨"); return; }
              const chosenColor = hasColors ? color : null;
              const chosenImg = hasColors
                ? (product.colors.find((c) => c.name === color)?.image_url || product.image_url)
                : product.image_url;
              add(product, qty, size || null, { silent: true, color: chosenColor, image_url: chosenImg });
              navigate("/checkout");
            }}
            className="mt-3 w-full bg-[#e8478e] hover:bg-[#c93875] hover:shadow-lg hover:-translate-y-0.5 text-white font-bold uppercase tracking-[0.2em] text-xs py-4 rounded-full transition-all"
          >
            Buy It Now <LightningBolt size={14} className="inline ml-1.5 align-middle"/>
          </button>

          <p className="mt-6 text-[#4a4239] leading-relaxed whitespace-pre-line" data-testid="product-description">{product.description}</p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="flex items-start gap-2 p-4 bg-white border border-[#e5dccd] rounded-2xl">
              <Truck size={16} className="text-[#2eb6a8] mt-0.5"/>
              <div><div className="font-bold uppercase tracking-[0.18em] text-[#1c1815]">Handmade To Order</div><div className="text-[#8a8076]">3-5 business days to ship</div></div>
            </div>
            <div className="flex items-start gap-2 p-4 bg-white border border-[#e5dccd] rounded-2xl">
              <Download size={16} className="text-[#e8478e] mt-0.5"/>
              <div><div className="font-bold uppercase tracking-[0.18em] text-[#1c1815]">Instant Files</div><div className="text-[#8a8076]">Digital items deliver on order</div></div>
            </div>
            <div className="flex items-start gap-2 p-4 bg-white border border-[#e5dccd] rounded-2xl">
              <ShieldCheck size={16} className="text-[#9c6dde] mt-0.5"/>
              <div><div className="font-bold uppercase tracking-[0.18em] text-[#1c1815]">Secure</div><div className="text-[#8a8076]">Safe & easy checkout</div></div>
            </div>
          </div>
        </div>
      </div>

      <ProductReviews
        product={product}
        onChange={() => api.get(`/products/${slug}`).then(({ data }) => setProduct(data)).catch(() => {})}
      />

      <RecentlyViewed excludeSlug={product.slug}/>

      {lightboxOpen && (
        <ImageZoomLightbox
          images={allImages}
          startIndex={Math.max(0, allImages.indexOf(current))}
          alt={product.title}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}
